import React from "react";
import { useState, useEffect, useContext } from "react";
import { Row, Col, Form, Stack, Alert, Spinner } from "react-bootstrap";
import { getFeedPerDecksForConsignment, getAllDetailedObservationsConsignmentID, getLoadedFeedOfConsignment } from "../../api/apiAccess";
import { FeedPerDeck } from "../../classes/FeedPerDeck";
import JsonToHtmlTable from "../../components/JsonToHtmlTable";
import { sortByProperty, sortArrayInStandardDeckOrder } from "../../utils/ListUtils";
import { EditViewString } from "../../components/ReadWriteElements/EditViewString";
import { UserContext } from "../..";
import { IsNullOrUndefined } from "../../utils/StringUtils";

// See comment in DeckRecord.js in the classes folder in regards to empty decks
const ViewFeedPerDeck = ({ consignmentID, selectedColumnIndex, allowEditing, onSelectedColumnIndexChanged }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [jsonDataDisplayed, setJsonDataDisplayed] = useState({ "Head": [], "Body": [] });
    const [selectedFilter, setSelectedFilter] = useState("Feed per head");
    const [hasData, setHasData] = useState(true);
    const [errorGettingData, setErrorGettingData] = useState(false);
    const userCtx = useContext(UserContext);

    const calcFeedConsumption = (todayROB, prevROB) => {
        return {
            pellets: prevROB.pellets - todayROB.pellets,
            grain: prevROB.grain - todayROB.grain,
            roughage: prevROB.roughage - todayROB.roughage,
        }
    };

    // get data
    useEffect(() => {
        const fetchDeckRecords = async () => {
            let lResponse = await getFeedPerDecksForConsignment(consignmentID);
            let lResponse2 = await getAllDetailedObservationsConsignmentID(consignmentID);
            let loadedFeedResponse = await getLoadedFeedOfConsignment(consignmentID);

            if (lResponse?.status === 200 && lResponse2?.status === 200 && loadedFeedResponse?.status === 200) {
                if (lResponse.data.length > 0) {
                    let records = [];
                    lResponse.data.forEach(d => {
                        records.push(new FeedPerDeck(d));
                    })

                    let lDateDayCombinations = getUniqueVoyageDaysThatHaveADeckRecord(records);
                    let lData = getFormattedData(lDateDayCombinations, records, lResponse2.data, loadedFeedResponse.data);

                    setJsonDataDisplayed(lData);
                    setHasData(true);
                }
                else {
                    setHasData(false);
                }
            }
            else {
                setErrorGettingData(true);
            }
            setIsLoading(false);
        }

        const getUniqueVoyageDaysThatHaveADeckRecord = (detailedDeckRecords) => {
            let lDays = [];

            detailedDeckRecords.forEach((record) => {
                let lExisitngDay = lDays.find(item => item.id === record.ConsignmentDay.id);
                if (!lExisitngDay) {
                    lDays.push(record.ConsignmentDay);
                }
            })
            lDays = sortByProperty(lDays, "dayNumber");
            return lDays;
        }

        const getFormattedData = (dateDayCombinations, detailedDeckRecords, observations, loadedFeed) => {
            if (dateDayCombinations && dateDayCombinations.length > 0 && detailedDeckRecords && detailedDeckRecords.length > 0) {
                let lHead = [
                    [["Deck"]],
                    [["Species"]]
                ];

                let lBody = [];

                let lConsumedPellets = 0;
                let lConsumedRoughage = 0;

                dateDayCombinations.forEach((aConsignmentDay) => {
                    let lDateHeader = [
                        ["Day " + aConsignmentDay.dayNumber],
                        createLink(aConsignmentDay, "/editfeedperdeck"),
                        ["Feed per head", "No.head", "Pellets", "Roughage", "Manual watering"]
                    ];
                    lHead.push(lDateHeader);

                    // get all detailed deck records fo x day
                    let lRecords = [...detailedDeckRecords].filter(dr => dr.ConsignmentDay.id === aConsignmentDay.id);

                    // sort in standard deck order
                    lRecords = sortArrayInStandardDeckOrder(lRecords, "ConsignmentDeck.deckName", "ConsignmentDeck.isBridge");

                    lRecords.forEach((record) => {
                        let lDeckName = record.ConsignmentDeck.deckName;
                        let lDeckID = record.ConsignmentDeck.id;

                        let lDeckRecordToUpdate = lBody.find(obj => obj.deckID === lDeckID);
                        if (!lDeckRecordToUpdate) {
                            lDeckRecordToUpdate = { groupRowHeading: lDeckName, groupRowsData: [], deckID: lDeckID };
                            lBody.push(lDeckRecordToUpdate);
                        }

                        let lSpeciesRecord = lDeckRecordToUpdate.groupRowsData.find(obj => obj.speciesID === record.Species.id);
                        // add new row for species if there is not one already
                        if (!lSpeciesRecord) {
                            let lHeading = record.Species.speciesName;
                            lSpeciesRecord = { rowHeading: lHeading, data: [], speciesID: record.Species.id };
                            lDeckRecordToUpdate.groupRowsData.push(lSpeciesRecord);
                        }

                        // If the deck was marked as empty on the ship display a hyphen
                        let lNumberOfHead = "-";
                        let lPellet = "-";
                        let lRoughage = "-";
                        let lfeedPerHead = "-";
                        let lManualWatering = "-";

                        lNumberOfHead = !IsNullOrUndefined(record.NumberOfHead) ? record.NumberOfHead : "";
                        lPellet = !IsNullOrUndefined(record.Pellets) ? record.Pellets + " t" : "";
                        lRoughage = !IsNullOrUndefined(record.Roughage) ? record.Roughage + " t" : "";
                        lfeedPerHead = !IsNullOrUndefined(record.NumberOfHead) ? (1000 * (Number(record.Pellets) + Number(record.Roughage)) / Number(record.NumberOfHead)).toFixed(2) : NaN;
                        lManualWatering = !IsNullOrUndefined(record.IsManualWatering) ? (record.IsManualWatering === true ? "Yes" : "No") : "";

                        let lDateData = [isNaN(lfeedPerHead) ? "" : lfeedPerHead + " kg/hd", lNumberOfHead, lPellet, lRoughage, lManualWatering];
                        lSpeciesRecord.data.push(lDateData);
                    })

                    const seen = new Set();
                    const speciesList = lRecords
                        .map(item => item.Species)
                        .filter(species => {
                            if (seen.has(species.id)) return false;
                            seen.add(species.id);
                            return true;
                        });

                    let totalNumberOfHead = 0;
                    let totalPellets = 0;
                    let totalRoughage = 0;
                    let totalFeedPerHead = 0;
                    speciesList.forEach((species) => {
                        let lDeckName = "";
                        let lDeckID = "specie-total";

                        let lDeckRecordToUpdate = lBody.find(obj => obj.deckID === lDeckID);
                        if (!lDeckRecordToUpdate) {
                            lDeckRecordToUpdate = { groupRowHeading: lDeckName, groupRowsData: [], deckID: lDeckID };
                            lBody.push(lDeckRecordToUpdate);
                        }

                        let lSpeciesRecord = lDeckRecordToUpdate.groupRowsData.find(obj => obj.speciesID === species.id);
                        // add new row for species if there is not one already
                        if (!lSpeciesRecord) {
                            let lHeading = species.speciesName + " Total";
                            lSpeciesRecord = { rowHeading: lHeading, data: [], speciesID: species.id };
                            lDeckRecordToUpdate.groupRowsData.push(lSpeciesRecord);
                        }

                        // If the deck was marked as empty on the ship display a hyphen
                        let lNumberOfHead = lRecords.filter(item => item.Species?.id === species.id).reduce((sum, item) => sum + (item.NumberOfHead || 0), 0);
                        let lPellet = lRecords.filter(item => item.Species?.id === species.id).reduce((sum, item) => sum + (item.Pellets || 0), 0);
                        let lRoughage = lRecords.filter(item => item.Species?.id === species.id).reduce((sum, item) => sum + (item.Roughage || 0), 0);
                        let lfeedPerHead = (1000 * (lPellet + lRoughage) / lNumberOfHead);

                        totalNumberOfHead += lNumberOfHead;
                        totalPellets += lPellet;
                        totalRoughage += lRoughage;

                        lConsumedPellets += totalPellets;
                        lConsumedRoughage += totalRoughage;

                        let lDateData = [lNumberOfHead, lPellet.toFixed(2) + " t", lRoughage.toFixed(2) + " t", isNaN(lfeedPerHead) ? "" : lfeedPerHead.toFixed(2) + " kg/hd", ""];
                        lSpeciesRecord.data.push(lDateData);
                    })

                    // Vessel Total
                    totalFeedPerHead = (1000 * (totalPellets + totalRoughage) / totalNumberOfHead);

                    let totalRows = lBody.find(obj => obj.deckID === 'vessel-total');
                    if (!totalRows) {
                        totalRows = { groupRowHeading: (<><strong>Total feed consumption</strong></>), groupRowsData: [], deckID: 'vessel-total' };
                        lBody.push(totalRows);
                    }

                    let totalRowData = totalRows.groupRowsData.find(obj => obj.speciesID === undefined);
                    if (!totalRowData) {
                        totalRowData = { rowHeading: (<div style={{ minWidth: "114px" }}><span>Daily feed </span><br /><span style={{ fontSize: "10px" }}>(Deck Records)</span></div>), data: [], speciesID: undefined };
                        totalRows.groupRowsData.push(totalRowData);
                    }

                    totalRowData.data.push(["" /*totalNumberOfHead*/, totalPellets.toFixed(2) + " t", totalRoughage.toFixed(2) + " t", isNaN(totalFeedPerHead) ? "" : totalFeedPerHead.toFixed(2) + " kg/hd", ""]);

                    // Daily feed (calculated from ROB)
                    const obs = observations.find(obj => obj.day.id === aConsignmentDay.id);
                    const prevDayObs = observations.find(obs => obs.day.dayNumber === aConsignmentDay.dayNumber - 1);
                    const feedConsumption = calcFeedConsumption(obs.feedDetail, prevDayObs ? prevDayObs.feedDetail : loadedFeed);

                    let calcFeedRowData = totalRows.groupRowsData.find(obj => obj.speciesID === "calc-robs");
                    if (!calcFeedRowData) {
                        calcFeedRowData = { rowHeading: (<><span>Daily feed </span><br /><span style={{ fontSize: "10px" }}>(calculated from ROB)</span></>), data: [], speciesID: "calc-robs" };
                        totalRows.groupRowsData.push(calcFeedRowData);
                    }
                    calcFeedRowData.data.push(["", (feedConsumption.pellets).toFixed(2) + " t", (feedConsumption.roughage).toFixed(2) + " t", "", ""]);
                })

                let lData = { "Head": lHead, "Body": lBody };
                return lData;
            }
            else {
                return { "Head": [], "Body": [] };
            }
        }

        fetchDeckRecords();
    }, [consignmentID]);

    const createLink = (aConsignmentDay, aPath) => {
        // If a user clicks edit we should take them to the same tab that they are viewing the previous deck record for
        let lText = EditViewString(userCtx.user.writeAccess);
        return [{ link: { href: aPath, text: lText, consignmentDay: aConsignmentDay } }];
    }

    return (
        <>
            {
                isLoading ? (
                    <div className="text-center pt-5">
                        <Spinner className="mx-auto" />
                        <div className="mx-auto">Loading data...</div>
                    </div>
                ) : null
            }
            {
                isLoading === false && hasData === true ?
                    <div>
                        <Row className="mb-3">
                            <Col>
                                <Stack gap={2} direction="horizontal" style={{ justifyContent: "end" }}>
                                    <div className="field-label">Compare to:</div>
                                    <Form.Select value={selectedFilter} onChange={(e) => setSelectedFilter(e.target.value)} style={{ maxWidth: "200px" }}>
                                        <option value="No.head">No.head</option>
                                        <option value="Pellets">Pellets</option>
                                        <option value="Roughage">Roughage</option>
                                        <option value="Feed per head">Feed per head</option>
                                        <option value="Manual watering">Manual watering</option>
                                    </Form.Select>
                                </Stack>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <JsonToHtmlTable jsonData={jsonDataDisplayed} filter={selectedFilter} duplicateHeaders={false} initialSelectedColumnIndex={!isNaN(selectedColumnIndex) ? selectedColumnIndex : undefined} showEdit={allowEditing} onSelectedColumnChanged={onSelectedColumnIndexChanged} />
                            </Col>
                        </Row>
                    </div> : null
            }
            {
                isLoading === false && hasData === false && errorGettingData === false ?
                    <Alert variant="info" className="field-label">There is currently no data for the selected consignment.</Alert>
                    : null
            }
            {
                isLoading === false && errorGettingData === true ?
                    <Alert variant="info" className="field-label">Something went wrong while fetching previous deck records.</Alert>
                    : null
            }
        </>
    )
};

export default ViewFeedPerDeck;